/**
 * Leads, oportunidades y avance del embudo.
 *
 * El CRM tenía ocho operaciones en la API y ninguna forma de invocarlas:
 * el embudo no podía ni empezar, porque no había forma de crear un lead.
 *
 * Incluye el campo que la visión del producto reivindica como diferenciador
 * —**la fecha de renovación del competidor**— y que casi ningún CRM del
 * sector captura: saber cuándo vence el contrato que tiene la competencia,
 * y cuántos meses antes hay que preavisar, es lo que convierte una visita
 * fría en una visita con fecha.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { FormSection, SelectField, TextField } from '../forms/fields.js';

interface Lead {
  id: string;
  code: string;
  legal_name: string;
  province: string | null;
  status: string;
  competitor_name: string | null;
  competitor_renewal_date: string | null;
}

export interface OpportunityRef {
  id: string;
  title: string;
  stage: string;
}

const SOURCES = [
  { value: 'VISITA_FRIA', label: 'Visita fría' },
  { value: 'RECOMENDACION', label: 'Recomendación' },
  { value: 'WEB', label: 'Web' },
  { value: 'TELEFONO', label: 'Teléfono' },
  { value: 'FERIA', label: 'Feria' },
  { value: 'LICITACION', label: 'Licitación' },
  { value: 'CAMPANA', label: 'Campaña' },
  { value: 'OTROS', label: 'Otros' },
];

const STAGES = [
  { value: 'LEAD', label: 'Lead' },
  { value: 'CONTACTADO', label: 'Contactado' },
  { value: 'VISITA', label: 'Visita' },
  { value: 'AUDITORIA', label: 'Auditoría' },
  { value: 'PROPUESTA', label: 'Propuesta' },
  { value: 'OFERTA_ENVIADA', label: 'Oferta enviada' },
  { value: 'NEGOCIACION', label: 'Negociación' },
  { value: 'GANADO', label: 'Ganado' },
  { value: 'PERDIDO', label: 'Perdido' },
];

const LOSS_REASONS = [
  { value: 'PRECIO', label: 'Precio' },
  { value: 'RENOVO_CON_ACTUAL', label: 'Renovó con su mantenedora actual' },
  { value: 'SIN_RESPUESTA', label: 'Sin respuesta' },
  { value: 'NO_ERA_DECISOR', label: 'No era quien decide' },
  { value: 'SIN_PRESUPUESTO', label: 'Sin presupuesto' },
  { value: 'PLAZO_INASUMIBLE', label: 'Plazo inasumible' },
  { value: 'FUERA_DE_ZONA', label: 'Fuera de zona' },
  { value: 'NO_CUALIFICADO', label: 'No cualificado' },
  { value: 'OTROS', label: 'Otros' },
];

export function CrmActions({
  opportunity,
  onDone,
}: {
  opportunity: OpportunityRef | null;
  onDone: () => void;
}): React.ReactNode {
  const { api, user } = useSession();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Lead
  const [legalName, setLegalName] = useState('');
  const [province, setProvince] = useState('Illes Balears');
  const [source, setSource] = useState('VISITA_FRIA');
  const [competitorName, setCompetitorName] = useState('');
  const [competitorRenewal, setCompetitorRenewal] = useState('');
  const [noticeMonths, setNoticeMonths] = useState('');

  // Oportunidad
  const [leadId, setLeadId] = useState('');
  const [title, setTitle] = useState('');
  const [value, setValue] = useState('');
  const [closeDate, setCloseDate] = useState('');

  // Avance
  const [stage, setStage] = useState('');
  const [lossReason, setLossReason] = useState('');
  const [stageNotes, setStageNotes] = useState('');

  const canWrite = hasPermission(user, 'crm.create');

  const reload = useCallback(async () => {
    const data = await api.get<{ items: Lead[] }>('/crm/leads').catch(() => ({ items: [] }));
    setLeads(data.items);
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function createLead(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await api.post('/crm/leads', {
        legalName,
        source,
        ...(province === '' ? {} : { province }),
        ...(competitorName === '' ? {} : { competitorName }),
        ...(competitorRenewal === '' ? {} : { competitorRenewalDate: competitorRenewal }),
        ...(noticeMonths === '' ? {} : { competitorNoticeMonths: Number(noticeMonths) }),
      });
      setNotice(`Lead ${legalName} creado.`);
      setLegalName('');
      setCompetitorName('');
      setCompetitorRenewal('');
      setNoticeMonths('');
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido crear el lead.');
    } finally {
      setBusy(false);
    }
  }

  async function createOpportunity(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await api.post('/crm/opportunities', {
        ...(leadId === '' ? {} : { leadId }),
        title,
        ...(value === '' ? {} : { estimatedValue: value }),
        ...(closeDate === '' ? {} : { expectedCloseDate: closeDate }),
      });
      setNotice(`Oportunidad «${title}» creada.`);
      setTitle('');
      setValue('');
      setCloseDate('');
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido crear la oportunidad.');
    } finally {
      setBusy(false);
    }
  }

  async function advance(): Promise<void> {
    if (!opportunity || stage === '') return;
    setError(null);
    setBusy(true);
    try {
      await api.post(`/crm/opportunities/${opportunity.id}/stage`, {
        stage,
        ...(stage === 'PERDIDO' && lossReason !== '' ? { lossReason } : {}),
        ...(stageNotes === '' ? {} : { notes: stageNotes }),
      });
      setNotice(`«${opportunity.title}» → ${STAGES.find((s) => s.value === stage)?.label ?? stage}.`);
      setStage('');
      setLossReason('');
      setStageNotes('');
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido avanzar la etapa.');
    } finally {
      setBusy(false);
    }
  }

  if (!canWrite) return null;

  const leadOptions = leads
    .filter((l) => l.status === 'ACTIVO')
    .map((l) => ({ value: l.id, label: `${l.code} · ${l.legal_name}` }));

  return (
    <>
      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      {opportunity !== null && (
        <section className="panel">
          <h3 className="panel__title">{opportunity.title}</h3>
          <p className="screen__note">
            Etapa actual <strong>{opportunity.stage}</strong>
          </p>
          <div className="form">
            <FormSection title="Avanzar de etapa">
              <SelectField
                label="Nueva etapa"
                value={stage}
                onChange={setStage}
                options={STAGES}
                emptyLabel="Seleccione…"
              />
              {stage === 'PERDIDO' && (
                <SelectField
                  label="Motivo de la pérdida"
                  value={lossReason}
                  onChange={setLossReason}
                  options={LOSS_REASONS}
                  emptyLabel="Seleccione…"
                  help="Es el dato que dice si se pierde por precio o por llegar tarde. Sin él, el embudo no enseña nada."
                  required
                />
              )}
              <TextField label="Notas" value={stageNotes} onChange={setStageNotes} />
              <button
                className="button button--primary"
                type="button"
                disabled={busy || stage === '' || (stage === 'PERDIDO' && lossReason === '')}
                onClick={() => void advance()}
              >
                {busy ? 'Guardando…' : 'Avanzar'}
              </button>
            </FormSection>
          </div>
        </section>
      )}

      <section className="panel">
        <h3 className="panel__title">Leads ({leads.length})</h3>

        {leads.length > 0 && (
          <div className="scroller">
            <table className="table__grid table__grid--compact">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Razón social</th>
                  <th>Provincia</th>
                  <th>Competidor</th>
                  <th>Renueva</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td>{l.code}</td>
                    <td>{l.legal_name}</td>
                    <td>{l.province ?? '—'}</td>
                    <td>{l.competitor_name ?? '—'}</td>
                    <td>{l.competitor_renewal_date ?? '—'}</td>
                    <td>
                      {l.status === 'ACTIVO' ? (
                        <span className="pill pill--ok">Activo</span>
                      ) : (
                        <span className="pill pill--off">{l.status.toLowerCase()}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="form">
          <FormSection title="Nuevo lead">
            <TextField label="Razón social" value={legalName} onChange={setLegalName} required />
            <TextField label="Provincia" value={province} onChange={setProvince} />
            <SelectField label="Origen" value={source} onChange={setSource} options={SOURCES} />
          </FormSection>

          <FormSection title="Contrato del competidor">
            <TextField
              label="Mantenedora actual"
              value={competitorName}
              onChange={setCompetitorName}
              help="Quién le lleva hoy el mantenimiento."
            />
            <TextField
              label="Fecha de renovación"
              type="date"
              value={competitorRenewal}
              onChange={setCompetitorRenewal}
              help="Cuándo vence su contrato. Es lo que convierte una visita fría en una visita con fecha."
            />
            <TextField
              label="Preaviso (meses)"
              type="number"
              value={noticeMonths}
              onChange={setNoticeMonths}
              help="Cuánto antes hay que avisar para no renovar. Marca la fecha límite real para presentar oferta."
            />
            <button
              className="button button--primary"
              type="button"
              disabled={busy || legalName === ''}
              onClick={() => void createLead()}
            >
              {busy ? 'Guardando…' : 'Crear lead'}
            </button>
          </FormSection>
        </div>
      </section>

      <section className="panel">
        <h3 className="panel__title">Nueva oportunidad</h3>
        <div className="form">
          <FormSection title="Datos">
            <SelectField
              label="Lead"
              value={leadId}
              onChange={setLeadId}
              options={leadOptions}
              emptyLabel="Sin lead asociado"
              help="Se puede crear sin lead si viene de un cliente que ya existe."
            />
            <TextField label="Título" value={title} onChange={setTitle} required />
            <TextField
              label="Valor estimado anual"
              value={value}
              onChange={setValue}
              help="En euros. Alimenta el valor del embudo."
            />
            <TextField
              label="Cierre previsto"
              type="date"
              value={closeDate}
              onChange={setCloseDate}
            />
            <button
              className="button button--primary"
              type="button"
              disabled={busy || title === ''}
              onClick={() => void createOpportunity()}
            >
              {busy ? 'Creando…' : 'Crear oportunidad'}
            </button>
          </FormSection>
        </div>
      </section>
    </>
  );
}
